import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  BellIcon,
  CirclesDeco,
  LockIcon,
  MessageIcon,
  RadarChart,
  RadialProgress,
} from "../../assets/icons/icons";

import teamTrustLogo from "../../assets/images/team-trust-logo.png";
import Dropdown from "../../components/common/Dropdown";
import Field from "../../components/common/Field";
import useOrganization from "../organization/hooks/useOrganization";
import { useProfile } from "./hooks/useProfile";
import { useFormik } from "formik";
import { createAccount } from "./api/auth.api";
import { setNewUserComplete } from "./store/login/reducer";
import { toast } from "react-toastify";

import { createAccountValidationSchema } from "./validations/createAccount.validation";
import useSettings from "../settings/hooks/useSettings";
const CreateAccount: React.FC = () => {
  document.title = `Create Account - ${process.env.REACT_APP_NAME}`;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { birthYearOptions, genderOptions, raceOptions } = useSettings();

  const { currentOrganization } = useOrganization();
  const { user } = useProfile();

  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
      year: "",
      gender: "",
      race: "",
      accepted: false,
    },
    validationSchema: createAccountValidationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const response = await createAccount(values);
        if (response?.success) {
          dispatch(setNewUserComplete());
          navigate("/signup-complete");
          window.location.reload();
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message ?? "Something went wrong");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const fieldError = (name: keyof typeof formik.values) =>
    formik.touched[name] && formik.errors[name] ? (
      <p className="form-error">{formik.errors[name]}</p>
    ) : null;

  return (
    <div className="login-page">
      <div className="login-left">
        <header className="create-account-header">
          <img
            src={currentOrganization?.image ?? teamTrustLogo}
            alt="Good Leadership"
            className="login-logo"
          />
        </header>

        <div className="login-body">
          <div className="login-card max-w-[444px]">
            <h1 className="login-title">
              Welcome to Team
              <br />
              Trust Accelerator, {user?.first_name}!
            </h1>
            <p className="login-description">
              This is the place where individual insights will help your team
              win together, faster and easier.
            </p>

            <form
              onSubmit={formik.handleSubmit}
              className="flex flex-col gap-5 max-w-[315px]"
            >
              {/* Password */}
              <Field label="Set a New Password">
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Password"
                  className="login-input"
                />
                {fieldError("password")}
              </Field>

              {/* Confirm Password */}
              <Field label="Confirm New Password">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Password"
                  className="login-input"
                />
                {fieldError("confirmPassword")}
              </Field>

              {/* Birth Year */}
              <Field label="Select your birth year (optional)">
                <Dropdown
                  id="year"
                  options={birthYearOptions}
                  value={formik.values.year}
                  onChange={(val) => formik.setFieldValue("year", val)}
                  placeholder="Select year"
                />
                {fieldError("year")}
              </Field>

              {/* Gender */}
              <Field label="Select your gender (optional)">
                <Dropdown
                  id="gender"
                  options={genderOptions}
                  value={formik.values.gender}
                  onChange={(val) => formik.setFieldValue("gender", val)}
                  placeholder="Select gender"
                />
                {fieldError("gender")}
              </Field>

              {/* Race */}
              <Field label="Select your race (optional)">
                <Dropdown
                  id="race"
                  options={raceOptions}
                  value={formik.values.race}
                  onChange={(val) => formik.setFieldValue("race", val)}
                  placeholder="Select race"
                />
                {fieldError("race")}
              </Field>

              {/* Terms & Conditions */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="accepted"
                    checked={formik.values.accepted}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="login-checkbox mt-0.5 flex-shrink-0"
                  />
                  <span className="login-remember-text">
                    Do you accept the{" "}
                    <Link
                      to="/communication-usage-terms"
                      className="underline hover:text-blue transition"
                      style={{ color: "var(--blue)" }}
                    >
                      Terms and Conditions?
                    </Link>
                  </span>
                </label>
                {fieldError("accepted")}
              </div>

              <button
                type="submit"
                className="login-submit"
                disabled={!formik.values.accepted || formik.isSubmitting}
              >
                {isLoading ? "Creating account..." : "Create an account"}
              </button>
            </form>

            {/* Footer links */}
            <div className="login-footer-links">
              <p
                className="text-slate-500 mt-2"
                style={{
                  fontFamily: "var(--font-primary)",
                  fontSize: "0.75rem",
                }}
              >
                By signing up, you agree to{" "}
                <Link
                  to="/communication-usage-terms"
                  className="underline hover:text-blue transition"
                  style={{ color: "var(--blue)" }}
                >
                  our communication &amp; usage terms
                </Link>
              </p>
            </div>
          </div>

          {/* Info text (tablet / mobile only) */}
          <div className="login-info">
            <h2 className="login-info-title">
              Build winning teams,
              <br />
              faster and easier.
            </h2>
            <p className="login-info-text">Thrive and win together.</p>
          </div>
        </div>

        {/* Desktop footer */}
        <footer className="login-desktop-footer">
          <p
            className="text-slate-500"
            style={{ fontFamily: "var(--font-primary)", fontSize: "0.75rem" }}
          >
            By signing up, you agree to{" "}
            <Link
              to="/communication-usage-terms"
              className="underline hover:text-blue transition"
              style={{ color: "var(--blue)" }}
            >
              our communication &amp; usage terms
            </Link>
          </p>
        </footer>
      </div>

      {/* ── RIGHT PANEL ─────────────────────────────────────────────────────── */}
      <div className="login-right">
        <CirclesDeco />

        <div
          className="relative mx-auto w-full px-4 py-11"
          style={{ maxWidth: 786 }}
        >
          <div
            className="relative mx-auto"
            style={{ maxWidth: 525, paddingTop: 77 }}
          >
            {/* Floating icon — Message */}
            <div
              className="absolute flex items-center justify-center rounded-full bg-white z-10"
              style={{
                top: 0,
                left: 147,
                width: 70,
                height: 70,
                fontSize: 70,
                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
              }}
            >
              <MessageIcon className="text-dark-blue" />
            </div>

            {/* Floating icon — Bell */}
            <div
              className="absolute flex items-center justify-center rounded-full bg-white z-10"
              style={{
                top: 107,
                left: -15,
                width: 101,
                height: 101,
                fontSize: 101,
                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
              }}
            >
              <BellIcon className="text-dark-blue" />
            </div>

            {/* Floating icon — Lock */}
            <div
              className="absolute flex items-center justify-center rounded-full bg-white z-10"
              style={{
                bottom: 13,
                right: 59,
                width: 72,
                height: 72,
                fontSize: 72,
                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
              }}
            >
              <LockIcon className="text-dark-blue" />
            </div>

            {/* Radial progress */}
            <div
              style={{
                position: "relative",
                marginLeft: "auto",
                maxWidth: 272,
                zIndex: 3,
                paddingBottom: 49,
              }}
            >
              <RadialProgress />
            </div>

            {/* Radar chart card */}
            <div
              className="relative bg-white"
              style={{
                marginTop: -92,
                maxWidth: 301,
                zIndex: 2,
                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  position: "relative",
                  paddingTop: `${(233 / 301) * 100}%`,
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center p-3">
                  <RadarChart />
                </div>
              </div>
            </div>
          </div>

          {/* Tagline */}
          <div
            className="relative mx-auto text-center px-4"
            style={{ marginTop: 77, maxWidth: 400, zIndex: 1 }}
          >
            <h2
              className="font-bold text-dark-blue leading-snug mb-6"
              style={{ fontSize: 35, fontFamily: "var(--font-primary)" }}
            >
              Build winning teams,
              <br />
              faster and easier.
            </h2>
            <p
              className="text-dark-blue"
              style={{ fontSize: 18, fontFamily: "var(--font-primary)" }}
            >
              Thrive and win together.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
